import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { handleApiError } from '../../utils/errorHandler';
import Modal from '../Modal';
import {
  discountsApi,
  DiscountDto,
  DiscountType,
  DiscountTypeOptions,
} from '../../services/discountsService';

interface DiscountFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  clientId: number;
  discount?: DiscountDto | null; // עריכה אם קיים, אחרת יצירה
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  color: var(--text-primary);
  font-weight: 500;
  font-size: 0.9rem;
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: 0.75rem;
  border: 1px solid ${props => (props.$hasError ? 'var(--error-color)' : 'var(--border-color)')};
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;

  &:focus {
    outline: none;
    border-color: ${props => (props.$hasError ? 'var(--error-color)' : 'var(--primary-color)')};
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  background: var(--surface-color);

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Textarea = styled.textarea`
  padding: 0.75rem;
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  font-size: 1rem;
  direction: rtl;
  text-align: right;
  resize: vertical;
  min-height: 70px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px var(--primary-light);
  }
`;

const Hint = styled.span`
  color: var(--text-secondary);
  font-size: 0.8rem;
`;

const ErrorMessage = styled.span`
  color: var(--error-color);
  font-size: 0.8rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 10px 20px;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.2s ease;

  ${props =>
    props.variant === 'primary'
      ? `background: var(--primary-color); color: #fff;
         &:hover:not(:disabled) { background: var(--primary-hover); }`
      : `background: var(--surface-color); color: var(--text-primary); border-color: var(--border-color);
         &:hover { background: var(--hover-background); }`}

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const toDateInput = (iso?: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const m = `${d.getMonth() + 1}`.padStart(2, '0');
  const day = `${d.getDate()}`.padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
};

const todayInput = (): string => toDateInput(new Date().toISOString());

const DiscountFormModal: React.FC<DiscountFormModalProps> = ({
  isOpen,
  onClose,
  onSaved,
  clientId,
  discount,
}) => {
  const isEdit = !!discount;

  const [amount, setAmount] = useState('');
  const [discountDate, setDiscountDate] = useState('');
  const [type, setType] = useState<number>(DiscountType.OneTime);
  const [endDate, setEndDate] = useState('');
  const [notes, setNotes] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const isMonthly = type === DiscountType.Monthly;

  // אתחול הטופס בכל פתיחה
  useEffect(() => {
    if (!isOpen) return;
    if (discount) {
      setAmount(String(discount.amount));
      setDiscountDate(toDateInput(discount.discountDate));
      setType(discount.type);
      setEndDate(toDateInput(discount.endDate));
      setNotes(discount.notes ?? '');
    } else {
      setAmount('');
      setDiscountDate(todayInput());
      setType(DiscountType.OneTime);
      setEndDate('');
      setNotes('');
    }
    setErrors({});
  }, [isOpen, discount]);

  const validateField = (name: string): string | null => {
    switch (name) {
      case 'amount': {
        if (!amount.trim()) return 'יש להזין סכום';
        const num = Number(amount);
        if (isNaN(num)) return 'הסכום אינו תקין';
        if (num <= 0) return 'הסכום חייב להיות גדול מאפס';
        return null;
      }
      case 'discountDate':
        if (!discountDate) return 'יש לבחור תאריך';
        return null;
      case 'endDate':
        if (isMonthly && endDate && discountDate && endDate < discountDate)
          return 'תאריך הסיום חייב להיות מאוחר מתאריך ההנחה';
        return null;
      default:
        return null;
    }
  };

  const setFieldError = (name: string) => {
    const err = validateField(name);
    setErrors(prev => {
      const next = { ...prev };
      if (err) next[name] = err;
      else delete next[name];
      return next;
    });
  };

  const clearError = (name: string) => {
    setErrors(prev => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateAll = (): boolean => {
    const newErrors: Record<string, string> = {};
    ['amount', 'discountDate', 'endDate'].forEach(f => {
      const err = validateField(f);
      if (err) newErrors[f] = err;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAll()) return;

    const payload = {
      amount: Number(amount),
      discountDate,
      type,
      endDate: isMonthly && endDate ? endDate : null,
      notes: notes.trim() || undefined,
    };

    try {
      setSaving(true);
      if (isEdit && discount) {
        await discountsApi.update(discount.id, payload);
        toast.success('ההנחה עודכנה בהצלחה');
      } else {
        await discountsApi.create({ clientId, ...payload });
        toast.success('ההנחה נוספה בהצלחה');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(handleApiError(err, 'אירעה שגיאה בשמירת ההנחה'));
    } finally {
      setSaving(false);
    }
  };

  const footer = (
    <>
      <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
        ביטול
      </Button>
      <Button type="submit" form="discount-form" variant="primary" disabled={saving}>
        {saving ? 'שומר...' : isEdit ? 'עדכון' : 'הוספה'}
      </Button>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'עריכת הנחה' : 'הוספת הנחה'}
      width="480px"
      footer={footer}
    >
      <Form id="discount-form" onSubmit={handleSubmit}>
        <FormGroup>
          <Label>סכום ההנחה (₪)</Label>
          <Input
            type="number"
            step="0.01"
            min="0"
            value={amount}
            $hasError={!!errors.amount}
            onChange={e => {
              setAmount(e.target.value);
              clearError('amount');
            }}
            onBlur={() => setFieldError('amount')}
            placeholder="0.00"
          />
          {errors.amount && <ErrorMessage>{errors.amount}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>תאריך</Label>
          <Input
            type="date"
            value={discountDate}
            $hasError={!!errors.discountDate}
            onChange={e => {
              setDiscountDate(e.target.value);
              clearError('discountDate');
            }}
            onBlur={() => setFieldError('discountDate')}
          />
          {errors.discountDate && <ErrorMessage>{errors.discountDate}</ErrorMessage>}
        </FormGroup>

        <FormGroup>
          <Label>סוג הנחה</Label>
          <Select
            value={type}
            onChange={e => {
              setType(Number(e.target.value));
              clearError('endDate');
            }}
          >
            {DiscountTypeOptions.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormGroup>

        {isMonthly && (
          <FormGroup>
            <Label>תאריך סיום (אופציונלי)</Label>
            <Input
              type="date"
              value={endDate}
              $hasError={!!errors.endDate}
              onChange={e => {
                setEndDate(e.target.value);
                clearError('endDate');
              }}
              onBlur={() => setFieldError('endDate')}
            />
            {errors.endDate ? (
              <ErrorMessage>{errors.endDate}</ErrorMessage>
            ) : (
              <Hint>ללא תאריך סיום - ההנחה תימשך עד סוף התשלומים של הלקוח</Hint>
            )}
          </FormGroup>
        )}

        <FormGroup>
          <Label>הערות</Label>
          <Textarea
            value={notes}
            maxLength={500}
            onChange={e => setNotes(e.target.value)}
            placeholder="הערות (אופציונלי)"
          />
        </FormGroup>
      </Form>
    </Modal>
  );
};

export default DiscountFormModal;
